/**
 * Índice e quadros da fumaça publicados pela máquina do agendador
 * (`AmaView-dados/agendador/fumaca`).
 *
 * A NOAA publica o produto ABI-L2-ADPF (detecção de aerossóis do GOES-19,
 * disco completo) como netCDF de ~4 MB a cada 10 min — grande demais e num
 * formato que o navegador não lê. A máquina lê só a máscara `Smoke` do setor
 * NSA e publica o contorno de cada quadro como GeoJSON (~5–20 KB comprimido),
 * das últimas 48 h, com CORS `*`. Nada é filtrado: cada pixel que a NOAA
 * marcou como fumaça está dentro de um polígono.
 */
import { fetchWithRetry } from '../lib/fetchRetry';
import { parseStarStamp } from '../goes/stamp';

/** Onde a máquina publica. `VITE_FUMACA_URL` aponta para outro lugar (vazio desliga a camada). */
export const BASE_FUMACA = (import.meta.env.VITE_FUMACA_URL ?? 'https://147.15.84.134/fumaca').replace(/\/$/, '');

export const NOAA_ADP_URL = 'https://noaa-goes19.s3.amazonaws.com/index.html#ABI-L2-ADPF/';

export const FUMACA_ATRIBUICAO = 'Fumaça: NOAA — GOES-19 ABI (ADP)';

export interface QuadroFumaca {
  /** Início da varredura (epoch ms, UTC) — o mesmo horário do quadro do STAR. */
  t: number;
  /** Carimbo AAAADDDHHMM (nome do arquivo). */
  c: string;
  /** Áreas de fumaça (polígonos) no setor. */
  n: number;
  /** Área somada (km²). */
  km2: number;
  /**
   * Fração do setor em que a NOAA tentou detectar fumaça (0–1), ou null se
   * não informada. De noite fica em 0: "sem fumaça" não é "sem dado".
   */
  cob: number | null;
}

export interface IndiceFumaca {
  /** Quando a máquina escreveu o índice (epoch ms), ou null. */
  gerado: number | null;
  /** Quadros em ordem crescente de tempo. */
  quadros: QuadroFumaca[];
}

/** Interpreta o índice; entradas inválidas são descartadas. Lança erro só quando o arquivo inteiro não serve. */
export function lerIndice(cru: unknown): IndiceFumaca {
  const m = cru as { gerado?: unknown; quadros?: unknown };
  if (!m || typeof m !== 'object' || !Array.isArray(m.quadros)) throw new Error('índice da fumaça sem a lista `quadros`');
  const vistos = new Set<number>();
  const quadros: QuadroFumaca[] = [];
  for (const q of m.quadros as Record<string, unknown>[]) {
    if (!q || typeof q.c !== 'string' || !/^\d{11}$/.test(q.c)) continue;
    const t = parseStarStamp(q.c);
    if (t === null || vistos.has(t)) continue;
    vistos.add(t);
    const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : 0);
    quadros.push({
      t,
      c: q.c,
      n: Math.round(num(q.n)),
      km2: num(q.km2),
      cob: typeof q.cob === 'number' && Number.isFinite(q.cob) ? Math.min(1, Math.max(0, q.cob)) : null,
    });
  }
  quadros.sort((a, b) => a.t - b.t);
  const gerado = typeof m.gerado === 'string' ? Date.parse(m.gerado) : NaN;
  return { gerado: Number.isFinite(gerado) ? gerado : null, quadros };
}

/** O índice muda a cada quadro novo; `no-cache` revalida no servidor (ele responde 304 quando nada mudou). */
export async function carregarIndice(signal?: AbortSignal): Promise<IndiceFumaca> {
  const cru = await fetchWithRetry<unknown>(`${BASE_FUMACA}/v2/indice.json`, { cache: 'no-cache', mode: 'cors', credentials: 'omit' }, {
    signal,
    read: (r) => r.json() as Promise<unknown>,
  });
  return lerIndice(cru);
}

export function urlQuadro(c: string, base: string = BASE_FUMACA): string {
  return `${base}/v2/quadros/${c}.geojson`;
}

/** Baixa um quadro (imutável: o cache HTTP do navegador guarda). */
export async function carregarQuadro(c: string, signal?: AbortSignal): Promise<unknown> {
  return fetchWithRetry<unknown>(urlQuadro(c), { mode: 'cors', credentials: 'omit' }, {
    signal,
    read: (r) => r.json() as Promise<unknown>,
  });
}
